import { useEffect, useRef, useState } from 'react';
import { useGeofenceDrawing } from './GeofenceEditor';
import { createPortal } from 'react-dom';
import {
  cssVar, dot, fitCoords, flagButton, GEOFENCE_FILL_OPACITY, geofenceCentre, geofenceColour, geofenceLabel,
  GROUP_KEY, GROUP_MAX_ZOOM, groupElement, groupNear, IRELAND, NAMES_KEY,
  numberElement, paintGroup, paintVehicle, Props, readFlag, SAT_KEY, saveFlag, stopElement, vehicleElement, zoomBand,
} from './mapCommon';

// Google Maps (decision 9C) through the Maps JavaScript API: road map in light or dark (the
// Map ID's cloud styles), satellite with roads and names. Google draws its own logo and
// copyright. Our markers are the same DOM elements as on the OpenStreetMap map.

export interface GoogleConfig { key: string; mapId: string }

// Loads Google's script once per page. Rejects if it can't load or the key is refused, so the
// caller can fall back to the OpenStreetMap map.
let loading: Promise<void> | null = null;
let refused: (() => void) | null = null;
export function loadGoogle(cfg: GoogleConfig): Promise<void> {
  return (loading ??= new Promise<void>((resolve, reject) => {
    const w = window as unknown as Record<string, unknown>;
    w.__ffGoogleReady = () => resolve();
    // Google calls this when the key is refused (wrong site, API not enabled, billing).
    w.gm_authFailure = () => { reject(new Error('Google refused the map key')); refused?.(); };
    const s = document.createElement('script');
    s.src = 'https://maps.googleapis.com/maps/api/js?' + new URLSearchParams({
      key: cfg.key, v: 'quarterly', loading: 'async', libraries: 'marker', language: 'en-GB', region: 'IE', callback: '__ffGoogleReady',
    });
    s.async = true;
    s.onerror = () => reject(new Error('Google Maps did not load'));
    document.head.append(s);
  }));
}
// Lets FleetMap switch to OpenStreetMap if the key is refused after the map was drawn.
export const onGoogleRefused = (fn: (() => void) | null) => { refused = fn; };

type Ll = { lat: number; lon: number };
const pos = (p: Ll) => ({ lat: p.lat, lng: p.lon });

// Advanced markers hang their content above the position (bottom centre); markers and pins
// are wrapped so their centre sits on it, as on the OpenStreetMap map.
function centred(el: HTMLElement): HTMLDivElement {
  const w = dot('gm-center');
  w.append(el);
  return w;
}

interface VehMarker { mk: google.maps.marker.AdvancedMarkerElement; el: HTMLDivElement; at: Ll }

export default function GoogleMap({ cfg, vehicles = [], selectedId = null, follow = false, onSelect, route = null, stops = [], numbered = [], fitKey, ready = true, popup, onClosePopup, markerOptions = false, geofences, showGeofences = true, drawing = null, goTo = null }: Props & { cfg: GoogleConfig }) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const [mapObj, setMapObj] = useState<google.maps.Map | null>(null);
  const overlay = useRef<google.maps.OverlayView | null>(null);
  const markers = useRef(new Map<number, VehMarker>());
  const groups = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const namesOn = useRef(!markerOptions || readFlag(NAMES_KEY, true));
  const groupOn = useRef(markerOptions && readFlag(GROUP_KEY, false));
  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;
  const extra = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const geoShapes = useRef<google.maps.Polygon[]>([]);
  const geoLabels = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const lines = useRef<google.maps.Polyline[]>([]);
  const popupRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const [popupEl] = useState(() => document.createElement('div'));
  const onClosePopupRef = useRef(onClosePopup);
  onClosePopupRef.current = onClosePopup;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const lastFit = useRef<string | number | undefined>(undefined);
  // The first move is instant and only then is the map shown; later moves animate.
  const [placed, setPlaced] = useState(false);
  const placedRef = useRef(false);
  const reveal = () => {
    if (placedRef.current) return;
    placedRef.current = true;
    setPlaced(true);
  };

  // Shows these points, no closer than maxZoom. Google's fitBounds has no zoom limit, and a
  // single point would zoom right in, so a tiny area is centred instead.
  const fitTo = (pts: Ll[], padding: number, maxZoom: number) => {
    const m = map.current;
    if (!m || !pts.length) return;
    const b = new google.maps.LatLngBounds();
    pts.forEach((p) => b.extend(pos(p)));
    const ne = b.getNorthEast();
    const sw = b.getSouthWest();
    if (Math.abs(ne.lat() - sw.lat()) < 0.002 && Math.abs(ne.lng() - sw.lng()) < 0.002) {
      m.setCenter(b.getCenter());
      m.setZoom(maxZoom);
      return;
    }
    m.fitBounds(b, padding);
    google.maps.event.addListenerOnce(m, 'idle', () => { if ((m.getZoom() ?? 0) > maxZoom) m.setZoom(maxZoom); });
  };

  // Create the map once.
  useEffect(() => {
    const dark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    let sat = readFlag(SAT_KEY, false);
    const m = new google.maps.Map(box.current!, {
      mapId: cfg.mapId,
      colorScheme: dark ? google.maps.ColorScheme.DARK : google.maps.ColorScheme.LIGHT,
      mapTypeId: sat ? 'hybrid' : 'roadmap',
      disableDefaultUI: true,
      gestureHandling: 'greedy',
      clickableIcons: false,
      maxZoom: 20,
    });
    m.fitBounds({ west: IRELAND.w, south: IRELAND.s, east: IRELAND.e, north: IRELAND.n }, 0);
    const corner = dot('map-ctrl map-layer gm-ctrl');
    // Our own zoom buttons: Google's sit under its marker layer, so vehicle names could cover them.
    const zoom = dot('gm-zoom');
    const zoomBtn = (label: string, text: string, by: number) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', label);
      b.title = label;
      b.textContent = text;
      b.onclick = () => m.setZoom((m.getZoom() ?? 0) + by);
      return b;
    };
    zoom.append(zoomBtn('Zoom in', '+', 1), zoomBtn('Zoom out', '−', -1));
    corner.append(zoom);
    // Map / Satellite switch, remembered per browser.
    const toggle = document.createElement('button');
    const paint = () => {
      toggle.textContent = sat ? 'Map' : 'Satellite';
      toggle.setAttribute('aria-label', sat ? 'Show the road map' : 'Show satellite view');
      toggle.setAttribute('aria-pressed', String(sat));
    };
    toggle.type = 'button';
    toggle.className = 'map-layer-btn';
    toggle.onclick = () => {
      sat = !sat;
      saveFlag(SAT_KEY, sat);
      m.setMapTypeId(sat ? 'hybrid' : 'roadmap');
      paint();
    };
    paint();
    corner.append(toggle);
    const opts = dot('map-opts');
    if (markerOptions) {
      opts.append(
        flagButton('Names', 'Show or hide vehicle names', namesOn.current, (on) => {
          namesOn.current = on;
          saveFlag(NAMES_KEY, on);
          box.current!.dataset.names = on ? 'on' : 'off';
        }),
        flagButton('Group', 'Group vehicles that are close together', groupOn.current, (on) => {
          groupOn.current = on;
          saveFlag(GROUP_KEY, on);
          regroup();
        }),
      );
    }
    if (opts.childElementCount) corner.append(opts);
    m.controls[google.maps.ControlPosition.RIGHT_TOP].push(corner);
    const band = () => { box.current!.dataset.zoom = zoomBand(m.getZoom() ?? 0); };
    band();
    m.addListener('zoom_changed', band);
    box.current!.dataset.names = namesOn.current ? 'on' : 'off';
    // Screen positions for grouping come from an empty overlay's projection.
    const ov = new google.maps.OverlayView();
    ov.onAdd = () => {};
    ov.onRemove = () => {};
    ov.draw = () => regroup();
    ov.setMap(m);
    overlay.current = ov;
    let frame = 0;
    m.addListener('bounds_changed', () => { if (groupOn.current && !frame) frame = requestAnimationFrame(() => { frame = 0; regroup(); }); });
    // Route: a light casing under the line, as on the OpenStreetMap map.
    lines.current = [
      new google.maps.Polyline({ map: m, strokeColor: cssVar('--marker-outline') || '#ffffff', strokeWeight: 7, strokeOpacity: 0.9, zIndex: 1, clickable: false }),
      new google.maps.Polyline({ map: m, strokeColor: cssVar('--accent') || '#1f6fae', strokeWeight: 4, strokeOpacity: 1, zIndex: 2, clickable: false }),
    ];
    map.current = m;
    setMapObj(m);
    return () => {
      setMapObj(null);
      google.maps.event.clearInstanceListeners(m);
      ov.setMap(null);
      markers.current.forEach((v) => (v.mk.map = null));
      groups.current.forEach((g) => (g.map = null));
      extra.current.forEach((x) => (x.map = null));
      geoShapes.current.forEach((g) => g.setMap(null));
      geoLabels.current.forEach((l) => (l.map = null));
      geoShapes.current = [];
      geoLabels.current = [];
      lines.current.forEach((l) => l.setMap(null));
      if (popupRef.current) popupRef.current.map = null;
      map.current = null;
      overlay.current = null;
      markers.current.clear();
      groups.current = [];
      extra.current = [];
      popupRef.current = null;
    };
  }, []);

  useGeofenceDrawing(mapObj, drawing);
  useEffect(() => {
    if (!goTo || !map.current) return;
    map.current.setCenter({ lat: goTo.lat, lng: goTo.lon });
    map.current.setZoom(17);
  }, [goTo?.key]);

  // Geofences, under the route and markers; not clickable, so the map behaves as before.
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    geoShapes.current.forEach((g) => g.setMap(null));
    geoLabels.current.forEach((l) => (l.map = null));
    const on = showGeofences ? m : null;
    geoShapes.current = (geofences ?? []).map((g) => new google.maps.Polygon({
      map: on, paths: g.points.map(([lon, lat]) => ({ lat, lng: lon })), clickable: false, zIndex: 0,
      strokeColor: geofenceColour(g), strokeWeight: 2, strokeOpacity: 0.9, fillColor: geofenceColour(g), fillOpacity: GEOFENCE_FILL_OPACITY,
    }));
    geoLabels.current = (geofences ?? []).map((g) => new google.maps.marker.AdvancedMarkerElement({
      map: on, position: pos(geofenceCentre(g)), content: centred(geofenceLabel(g)), zIndex: 0,
    }));
  }, [geofences, showGeofences]);

  // Vehicle markers.
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    const seen = new Set<number>();
    for (const v of vehicles) {
      seen.add(v.id);
      let vm = markers.current.get(v.id);
      if (!vm) {
        const el = vehicleElement(() => onSelectRef.current?.(v.id));
        vm = { el, at: v, mk: new google.maps.marker.AdvancedMarkerElement({ map: m, position: pos(v), content: centred(el) }) };
        markers.current.set(v.id, vm);
      }
      if (vm.at.lat !== v.lat || vm.at.lon !== v.lon) { vm.mk.position = pos(v); vm.at = { lat: v.lat, lon: v.lon }; }
      paintVehicle(vm.el, v, v.id === selectedId);
      vm.mk.zIndex = v.id === selectedId ? 1000 : null;
    }
    for (const [id, vm] of markers.current) {
      if (!seen.has(id)) {
        vm.mk.map = null;
        markers.current.delete(id);
      }
    }
    regroup();
  }, [vehicles, selectedId]);

  // Grouping, redone as the map moves. The selected vehicle is never grouped. Uses refs only,
  // so the map's own handlers can call any copy of it.
  function regroup() {
    const m = map.current;
    const proj = overlay.current?.getProjection();
    if (!m || !proj) return;
    const found = groupOn.current && (m.getZoom() ?? 0) < GROUP_MAX_ZOOM
      ? groupNear([...markers.current].filter(([id]) => id !== selectedRef.current).flatMap(([id, vm]) => {
        const p = proj.fromLatLngToContainerPixel(pos(vm.at));
        return p ? [{ id, x: p.x, y: p.y }] : [];
      }))
      : [];
    const hidden = new Set<number>();
    found.forEach((ids, i) => {
      ids.forEach((id) => hidden.add(id));
      let gm = groups.current[i];
      if (!gm) {
        gm = new google.maps.marker.AdvancedMarkerElement({ zIndex: 500, content: centred(groupElement((ids) => {
          fitTo(ids.flatMap((id) => markers.current.get(id)?.at ?? []), 80, GROUP_MAX_ZOOM);
        })) });
        groups.current.push(gm);
      }
      const pts = ids.map((id) => markers.current.get(id)!.at);
      gm.position = { lat: pts.reduce((a, p) => a + p.lat, 0) / pts.length, lng: pts.reduce((a, p) => a + p.lon, 0) / pts.length };
      if (!gm.map) gm.map = m;
      paintGroup((gm.content as HTMLElement).firstElementChild as HTMLElement, ids,
        ids.map((id) => markers.current.get(id)!.el.querySelector('.veh-label b')!.textContent).join(', '));
    });
    for (const gm of groups.current.slice(found.length)) if (gm.map) gm.map = null;
    for (const [id, vm] of markers.current) vm.el.classList.toggle('grouped', hidden.has(id));
  }

  // Keep the selected vehicle in view while following.
  const selected = vehicles.find((v) => v.id === selectedId);
  useEffect(() => {
    const m = map.current;
    if (ready && follow && selected && m) {
      if ((m.getZoom() ?? 0) < 13) m.setZoom(13);
      if (placedRef.current) m.panTo(pos(selected));
      else m.setCenter(pos(selected));
      reveal();
    }
  }, [ready, follow, selected?.lat, selected?.lon, selectedId]);

  // The card above the selected vehicle; moves with it, closes with ×.
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    if (!popup || !selected) {
      if (popupRef.current) popupRef.current.map = null;
      popupRef.current = null;
      return;
    }
    if (!popupRef.current) {
      const card = dot('map-popup');
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'map-popup-close';
      close.setAttribute('aria-label', 'Close');
      close.textContent = '×';
      card.append(close, popupEl);
      const p = new google.maps.marker.AdvancedMarkerElement({ map: m, position: pos(selected), content: card, zIndex: 2000 });
      close.addEventListener('click', (e) => {
        e.stopPropagation();
        p.map = null;
        if (popupRef.current === p) { popupRef.current = null; onClosePopupRef.current?.(); }
      });
      popupRef.current = p;
      // Nudge the map so the card isn't cut off at the edge (the OpenStreetMap popup flips instead).
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!box.current || popupRef.current !== p) return;
        const r = card.getBoundingClientRect();
        const b = box.current.getBoundingClientRect();
        const gap = 10;
        const dx = r.left < b.left + gap ? r.left - b.left - gap : r.right > b.right - gap ? r.right - b.right + gap : 0;
        const dy = r.top < b.top + gap ? r.top - b.top - gap : 0;
        if (dx || dy) m.panBy(dx, dy);
      }));
    } else {
      popupRef.current.position = pos(selected);
    }
  }, [popup ? 'open' : 'closed', selectedId, selected?.lat, selected?.lon]);

  // Route, its ends, and stops.
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    const path = route && route.points.length > 1 ? route.points.map((p) => ({ lat: p[1], lng: p[2] })) : [];
    lines.current.forEach((l) => l.setPath(path));
    extra.current.forEach((x) => (x.map = null));
    extra.current = [];
    const add = (el: HTMLElement, p: Ll) => extra.current.push(new google.maps.marker.AdvancedMarkerElement({ map: m, position: pos(p), content: centred(el) }));
    // The journey's end is a ring, its start a filled dot.
    stops.forEach((s) => add(stopElement(), s));
    numbered.forEach((p, i) => add(numberElement(i + 1), p));
    if (route) {
      if (!numbered.length) add(dot('pin pin-start'), route.from);
      add(dot('pin pin-end'), route.to);
    }
  }, [route, stops, numbered]);

  // Fit to what's shown when asked to.
  useEffect(() => {
    if (!map.current || !ready || fitKey === undefined || fitKey === lastFit.current) return;
    const coords = fitCoords({ vehicles, route, stops, numbered, geofences });
    if (coords.length === 0) return;
    lastFit.current = fitKey;
    fitTo(coords.map(([lon, lat]) => ({ lat, lon })), 60, 15);
    reveal();
  }, [ready, fitKey, route, stops, vehicles, geofences]);

  // Ready but nothing to place (no positions at all): show the default view.
  useEffect(() => {
    if (ready) reveal();
  }, [ready]);

  return (
    <>
      <div ref={box} className="map" role="region" aria-label="Map" style={{ opacity: placed ? 1 : 0, transition: 'opacity .2s' }} />
      {popup && createPortal(popup, popupEl)}
    </>
  );
}
